/**
 * @fileoverview Labeled text field molecule.
 * @description Composes the Label and Input atoms with an optional error message, so any plain
 * text field in the app looks and behaves consistently. Forwards Input's size prop rather than
 * fixing one, so a caller in a smaller layout (e.g. a modal) still gets that consistency.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import Input from '@/shared/components/atoms/Input'
import Label from '@/shared/components/atoms/Label'

interface FormFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  id: string
  label: string
  error?: string | undefined
  size?: 'lg' | 'md'
  'data-testid'?: string
}

/**
 * @component
 * @param {FormFieldProps} props - Standard input attributes plus id/label/error/size.
 * @param {string} props.id - Also used to associate the label with the input.
 * @param {string} props.label - The visible field label.
 * @param {string} [props.error] - Validation message shown below the field, if any.
 * @param {'lg' | 'md'} [props.size] - Forwarded to the underlying Input atom.
 * @param {string} [props['data-testid']] - QA locator for the input. The error message gets the
 * same id with a trailing "-input" swapped for "-error" (e.g. login-email-input ->
 * login-email-error).
 */
function FormField({
  id,
  label,
  error,
  size = 'lg',
  'data-testid': testId,
  ...inputProps
}: FormFieldProps) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="mt-2">
        <Input
          id={id}
          size={size}
          invalid={Boolean(error)}
          aria-invalid={Boolean(error)}
          data-testid={testId}
          {...inputProps}
        />
      </div>
      {error && (
        <p
          className="mt-1.5 text-[13px] text-red-600"
          data-testid={testId ? `${testId.replace(/-input$/, '')}-error` : undefined}
        >
          {error}
        </p>
      )}
    </div>
  )
}

export default FormField
